import { usePartyMutation } from "Api/Mutations/Party/usePartyMutation";
import type { PartyDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { toast } from "sonner";

/**
 * Shared save path of the party-backed field dialogs (phone/email/VAT ID): merges the changed
 * field into the whole-party PUT (the spread preserves every other party field) and
 * handles the toasts. `onSuccess` receives the party as it was sent — the server may still
 * adjust it (a register-verified VAT ID), which the refetched party then shows. Callers
 * guard `party.publicID` before rendering their form.
 */
export const usePartyFieldSave = (
  party: PartyDto,
  onSuccess: (sentParty: PartyDto) => void,
) => {
  const { t } = useResource();

  const { mutate: updateParty, isPending } = usePartyMutation({
    onSuccess: ({ data }) => {
      toast.success(t(Resources.Common.Saved));
      onSuccess(data);
    },
    onError: () => toast.error(t(Resources.Components.Account.PartyError)),
  });

  const { publicID } = party;

  const saveParty = (changes: Partial<PartyDto>) => {
    if (!publicID) {
      return;
    }

    updateParty({ publicID, data: { ...party, ...changes } });
  };

  return { saveParty, isSaving: isPending };
};
